import { FaFacebookF, FaInstagram, FaTiktok, FaPhoneAlt } from "react-icons/fa";
import { HashLink } from "react-router-hash-link";

const OFFSET = 70;

const scrollWithOffset = (el) => {
  const y = el.getBoundingClientRect().top + window.pageYOffset - OFFSET;
  window.scrollTo({ top: y, behavior: "smooth" });
};

const Footer = () => {
  const anchors = [
    { to: "meista", label: "Meistä" },
    { to: "ohjelmisto", label: "Ohjelmisto" },
    { to: "media", label: "Media" },
    { to: "useinkysyttya", label: "UKK" },
    { to: "varaa", label: "Varaa" }
  ];

  const regionLinks = [
    { href: "/bilebandi-uusimaa", label: "Bilebändi Uusimaa" },
    { href: "/bilebandi-pirkanmaa", label: "Bilebändi Pirkanmaa" },
    { href: "/bilebandi-varsinais-suomi", label: "Bilebändi Varsinais-Suomi" },
    { href: "/bilebandi-kanta-hame", label: "Bilebändi Kanta-Häme" },
    { href: "/bilebandi-paijat-hame", label: "Bilebändi Päijät-Häme" },
    { href: "/bilebandi-kymenlaakso", label: "Bilebändi Kymenlaakso" },
    { href: "/bilebandi-etela-karjala", label: "Bilebändi Etelä-Karjala" },
    { href: "/bilebandi-etela-savo", label: "Bilebändi Etelä-Savo" },
    { href: "/bilebandi-etela-pohjanmaa", label: "Bilebändi Etelä-Pohjanmaa" },
    { href: "/bilebandi-satakunta", label: "Bilebändi Satakunta" }
  ];

  const cityLinks = [
    { href: "/bilebandi-helsinki", label: "Bilebändi Helsinki" },
    { href: "/bilebandi-vantaa", label: "Bilebändi Vantaa" },
    { href: "/bilebandi-espoo", label: "Bilebändi Espoo" },
    { href: "/bilebandi-kerava", label: "Bilebändi Kerava" },
    { href: "/bilebandi-jarvenpaa", label: "Bilebändi Järvenpää" },
    { href: "/bilebandi-tampere", label: "Bilebändi Tampere" },
    { href: "/bilebandi-turku", label: "Bilebändi Turku" },
    { href: "/bilebandi-lahti", label: "Bilebändi Lahti" },
    { href: "/bilebandi-hameenlinna", label: "Bilebändi Hämeenlinna" },
    { href: "/bilebandi-pori", label: "Bilebändi Pori" },
    { href: "/bilebandi-lappeenranta", label: "Bilebändi Lappeenranta" },
    { href: "/bilebandi-kouvola", label: "Bilebändi Kouvola" },
    { href: "/bilebandi-mikkeli", label: "Bilebändi Mikkeli" },
    { href: "/bilebandi-seinajoki", label: "Bilebändi Seinäjoki" }
  ];

  const ga = (name, data = {}) => {
    if (window.gaEvent) window.gaEvent(name, data);
  };

  const linkClass =
    "transition hover:text-joyrideCyan focus:text-joyrideCyan dark:hover:text-joyrideLight dark:focus:text-joyrideLight";

  const cardClass =
    "relative overflow-hidden rounded-3xl border border-joyrideNavy/10 bg-white/70 p-6 shadow-lg backdrop-blur-sm dark:border-white/10 dark:bg-white/5 dark:shadow-[0_0_35px_rgba(0,240,255,0.08)]";

  const socialClass =
    "grid h-11 w-11 place-items-center rounded-full border border-joyridePurple/20 bg-white/70 text-joyridePurple shadow-sm transition hover:-translate-y-1 hover:border-joyrideCyan hover:text-joyrideCyan hover:shadow-[0_0_22px_rgba(0,240,255,0.35)] focus:-translate-y-1 focus:border-joyrideCyan focus:text-joyrideCyan dark:border-white/10 dark:bg-white/10 dark:text-joyrideCyan dark:hover:text-joyrideLight";

  const renderFooterLinks = (links, eventName) => (
    <ul className="grid grid-cols-1 gap-x-6 gap-y-2 text-center text-sm sm:grid-cols-2 xl:grid-cols-3 xl:text-left">
      {links.map(({ href, label }) => (
        <li key={href} className="min-w-0">
          <a
            href={href}
            onClick={() => ga(eventName, { alue: label })}
            className={`inline-block break-normal underline underline-offset-4 ${linkClass}`}
          >
            {label}
          </a>
        </li>
      ))}
    </ul>
  );

  return (
    <footer
      role="contentinfo"
      className="
        relative overflow-hidden
        bg-gray-100 text-joyrideNavy
        py-14
        dark:bg-joyrideBars dark:text-joyrideCyan
        dark:shadow-[0_-4px_18px_-8px_rgba(0,240,255,0.35)]
      "
    >
      <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-joyridePurple via-joyrideCyan to-joyridePurple" />

      <div className="pointer-events-none absolute -left-28 top-10 h-72 w-72 rounded-full bg-joyrideCyan/15 blur-3xl" />
      <div className="pointer-events-none absolute -right-24 bottom-0 h-80 w-80 rounded-full bg-joyridePurple/20 blur-3xl" />
      <div className="pointer-events-none absolute left-1/2 top-1/2 h-40 w-40 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white/10 blur-3xl dark:bg-joyrideCyan/10" />

      <div className="relative mx-auto grid max-w-7xl grid-cols-1 gap-6 px-4 text-center md:grid-cols-3 md:text-left">
        <address className={`${cardClass} not-italic`}>
          <div className="pointer-events-none absolute -right-8 -top-10 h-24 w-24 rounded-full bg-joyrideCyan/10 blur-2xl" />

          <h3 className="mb-3 font-lobster text-3xl font-semibold text-joyridePurple dark:text-joyrideCyan dark:text-shadow-lg">
            Kontakti & tiedustelut
          </h3>

          <p className="mb-2">
            <a
              href="https://buukkeri.fi/useinkysyttya/"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => ga("buukkeri_ukk_klikattu")}
              className={`underline underline-offset-4 ${linkClass}`}
            >
              Varaus- ja peruutusehdot
            </a>
          </p>

          <p className="mb-5 text-sm opacity-90">
            <a
              href="https://buukkeri.fi/profiili/bilebandi-joyride/"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => ga("buukkeri_profiili_klikattu")}
              className={`underline underline-offset-4 ${linkClass}`}
            >
              Bilebändi Joyriden profiili Buukkerissa
            </a>
          </p>

          <p className="flex items-center justify-center gap-2 md:justify-start dark:text-shadow-sm">
            <a
              href="mailto:kontakti@bilebandijoyride.com"
              onClick={() => ga("sahkoposti_klikattu")}
              className={linkClass}
            >
              kontakti@bilebandijoyride.com
            </a>
          </p>

          <p className="mt-2 flex items-center justify-center gap-2 md:justify-start dark:text-shadow-sm">
            <FaPhoneAlt
              aria-hidden="true"
              className="text-joyridePurple dark:text-joyrideCyan"
            />
            <a
              href="tel:+358443519265"
              onClick={() => ga("puhelu_klikattu")}
              className={linkClass}
            >
              +358 44 351 9265
            </a>
          </p>
        </address>

        <nav className={cardClass} aria-label="Sivuston sisäinen navigaatio">
          <div className="pointer-events-none absolute -left-8 -bottom-10 h-24 w-24 rounded-full bg-joyridePurple/10 blur-2xl" />

          <h3 className="mb-3 font-lobster text-3xl font-semibold text-joyridePurple dark:text-joyrideCyan dark:text-shadow-lg">
            Navigointi
          </h3>

          <ul className="flex flex-col items-center space-y-2 md:items-start">
            {anchors.map(({ to, label }) => (
              <li key={to}>
                <HashLink
                  to={`/#${to}`}
                  scroll={scrollWithOffset}
                  className={`cursor-pointer dark:text-shadow-sm ${linkClass}`}
                >
                  {label}
                </HashLink>
              </li>
            ))}

            <li>
              <HashLink
                to="/privacy#top"
                scroll={scrollWithOffset}
                className={`cursor-pointer dark:text-shadow-sm ${linkClass}`}
              >
                Tietosuoja
              </HashLink>
            </li>
          </ul>
        </nav>

        <div className={`${cardClass} flex flex-col items-center md:items-end`}>
          <div className="pointer-events-none absolute -right-8 -bottom-10 h-28 w-28 rounded-full bg-joyrideCyan/10 blur-2xl" />

          <h3 className="mb-3 font-lobster text-3xl font-semibold text-joyridePurple dark:text-joyrideCyan dark:text-shadow-lg">
            Seuraa meitä
          </h3>

          <p className="mb-5 max-w-xs text-sm leading-relaxed text-joyrideNavy/75 md:text-right dark:text-joyrideLight/75">
            Kurkkaa keikkafiilistä, videoita ja tunnelmia Bilebändi Joyriden
            somekanavista.
          </p>

          <div className="flex justify-center space-x-4 md:justify-end">
            <a
              href="https://www.facebook.com/bilebandijoyride"
              target="_blank"
              rel="me noopener noreferrer"
              aria-label="Facebook"
              onClick={() => ga("some_klikattu", { kanava: "facebook" })}
              className={socialClass}
            >
              <FaFacebookF />
            </a>

            <a
              href="https://www.instagram.com/bilebandijoyride/"
              target="_blank"
              rel="me noopener noreferrer"
              aria-label="Instagram"
              onClick={() => ga("some_klikattu", { kanava: "instagram" })}
              className={socialClass}
            >
              <FaInstagram />
            </a>

            <a
              href="https://www.tiktok.com/@bilebandijoyride"
              target="_blank"
              rel="me noopener noreferrer"
              aria-label="TikTok"
              onClick={() => ga("some_klikattu", { kanava: "tiktok" })}
              className={socialClass}
            >
              <FaTiktok />
            </a>
          </div>
        </div>
      </div>

      <div className="relative mx-auto mt-8 max-w-7xl px-4">
        <nav
          className={`${cardClass} text-center`}
          aria-label="Bilebändi Joyriden keikkailualueet"
        >
          <h3 className="mb-3 text-center font-lobster text-3xl font-semibold text-joyridePurple dark:text-joyrideCyan dark:text-shadow-lg">
            Bilebändi Joyriden keikkailualueet
          </h3>

          <p className="mx-auto mb-6 max-w-3xl text-sm leading-relaxed text-joyrideNavy/75 dark:text-joyrideLight/75">
            Tutustu Bilebändi Joyriden aluekohtaisiin sivuihin ja tarkista,
            sopiiko bändi juuri sinun juhlapaikkakunnallesi.
          </p>

          <div className="grid gap-10 text-center xl:grid-cols-[1fr_1.25fr] xl:text-left">
            <section aria-labelledby="footer-maakunnat">
              <h4
                id="footer-maakunnat"
                className="mb-3 text-center text-base font-bold text-joyridePurple dark:text-joyrideCyan xl:text-left"
              >
                Maakunnat
              </h4>

              {renderFooterLinks(
                regionLinks,
                "maakuntalanderi_footer_klikattu"
              )}
            </section>

            <section aria-labelledby="footer-kaupungit">
            <h4
              id="footer-kaupungit"
              className="mb-3 text-center text-base font-bold text-joyridePurple dark:text-joyrideCyan xl:text-left"
            >
              Kaupungit
            </h4>

              {renderFooterLinks(
                cityLinks,
                "kaupunkilanderi_footer_klikattu"
              )}
            </section>
          </div>
        </nav>
      </div>

      <div className="relative mx-auto mt-8 max-w-7xl px-4">
        <div className="rounded-2xl border border-joyrideNavy/10 bg-white/50 px-4 py-4 text-center text-sm shadow-sm backdrop-blur-sm dark:border-white/10 dark:bg-white/5 dark:text-shadow-sm">
          © {new Date().getFullYear()} Bilebändi Joyride – Sivuston toteutus{" "}
          <a
            href="https://arttustenius.com"
            target="_blank"
            rel="noopener noreferrer"
            className={`underline underline-offset-4 ${linkClass}`}
          >
            Arttu Stenius
          </a>
        </div>
      </div>
    </footer>
  );
};

export default Footer;